import React, { useEffect, useRef, useState } from "react";
import instance from "api/apiClient";
import TextEditor from "components/wysiwyg/Wysiwyg";
import { useTranslation } from "react-i18next";
import s from "./TextLocalesTab.module.scss";
import cn from "clsx";
import ModalTabSkeleton from "components/global/Skeleton/ModalTabSkeleton/ModalTabSkeleton";

interface Props {
  blockId: number;
  localesList: string[];
  activeLocale: string;
  handleLocaleChange: (v: string) => void;
  value: string;
  handleData: (value: string) => void;
  onInitialData: (value: string) => void;
  fieldErrors?: Record<string, string>;
  clearFieldError: (field: string) => void;
  resetErrors?: () => void;
  readOnly?: boolean;
  onLoadingChange?: (isLoading: boolean) => void;
}

export const TextLocalesTab: React.FC<Props> = ({
  blockId,
  localesList,
  activeLocale,
  handleLocaleChange,
  value,
  handleData,
  onInitialData,
  fieldErrors,
  clearFieldError,
  resetErrors,
  readOnly,
  onLoadingChange,
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const requestVersion = useRef(0);
  const translationKey = `translations.${activeLocale}`;

  useEffect(() => {
    resetErrors?.();
  }, [activeLocale]);

  useEffect(() => {
    const version = ++requestVersion.current;
    if (!blockId || !activeLocale) {
      setLoading(false);
      return;
    }

    setLoading(true);
    handleData("");
    onInitialData("");
    instance
      .get(
        `/cms/footer/main-text/translation/${blockId}?locale=${activeLocale}`,
      )
      .then((res) => {
        if (version !== requestVersion.current) return;
        const content = res?.data?.content ?? "";
        handleData(content);
        onInitialData(content);
      })
      .catch((error) => {
        if (version !== requestVersion.current) return;
        handleData("");
        onInitialData("");
        if (error?.status !== 404) {
          console.error("Failed to load footer text translation", error);
        }
      })
      .finally(() => {
        if (version === requestVersion.current) setLoading(false);
      });

    return () => {
      requestVersion.current += 1;
    };
  }, [blockId, activeLocale, handleData, onInitialData]);

  useEffect(() => {
    onLoadingChange?.(loading);
  }, [loading, onLoadingChange]);

  const handleEditorChange = (newHtml: string) => {
    handleData(newHtml);
    clearFieldError(`translations.${activeLocale}`);
  };

  return (
    <div className={s.modalTabWrapper}>
      <div className="game-modal-tabs small">
        {localesList.map((loc) => (
          <div
            key={loc}
            className={`tab-item ${activeLocale === loc ? "active" : ""}`}
            onClick={() => {
              if (activeLocale !== loc) {
                setLoading(true);
                handleLocaleChange(loc);
              }
            }}
          >
            <img
              src={`https://flagpedia.net/data/flags/icon/16x12/${loc
                .split("-")[1]
                ?.toLowerCase()}.png`}
              alt=""
            />
            {t(loc).replace(/-[a-z]+$/i, "")}
          </div>
        ))}
      </div>
      {loading ? (
        <ModalTabSkeleton />
      ) : (
        <div
          className={cn("bonus-form-box--item html-editor", s.textTabEditor)}
        >
          <div className="label">{t("content")}</div>
          <TextEditor
            key={activeLocale}
            value={value ?? ""}
            onChange={handleEditorChange}
            hasError={Boolean(fieldErrors?.[translationKey])}
            disabled={readOnly}
          />
        </div>
      )}
    </div>
  );
};
